// Styles
import "./account-details-modal.scss";
// Tools
import { useEffect, useMemo, useState, type ReactNode } from "react";
// Components
import { Modal } from "../../../../../../../../../../shared/components/modal/Modal";
import { CompactButton } from "../../../../../../../../../../shared/components/buttons/compact-button/CompactButton";
import { CircleLoader } from "../../../../../../../../../../shared/components/circle-loader/CircleLoader";
import {
	SOCIAL_CASINO_GROUP_1_ISO,
	SOCIAL_CASINO_GROUP_2_ISO,
	type SocialCasinoGroupId,
} from "../../../../../../../../../../shared/constants/social-casino-groups";
import { AccountCardFetchError } from "../../../../../../../../errors/account-card-fetch-error";
import { useAccountCard } from "../../../../../../../../service/useAccountCard";
import {
	extractSaveCertificatesError,
	useSaveAccountCertificates,
} from "../../../../../../../../service/useSaveAccountCertificates";
import {
	useExtraGamblingGeos,
	usePriorityGamblingGeos,
} from "../../../../../../../../service/useGeoReference";
import { formatAccountCardId } from "../../../../../../../../../../shared/utils/normalize-account-card-id";
import {
	buildCertificatesDraft,
	buildSaveCertificatesPayload,
	draftsEqual,
	gamblingChipGeos,
	toggleGeo,
	toggleSocialGroup,
	type CertificatesDraft,
} from "./certificates-draft";
import { GeoSearchSelect } from "./GeoSearchSelect";
import { resolveCertPresence } from "../../utils/cert-presence";
import {
	MAX_APP_BUNDLE_ID_LENGTH,
	validateAppBundleId,
} from "./app-bundle-id";

type Props = {
	isOpen: boolean;
	accountId: string;
	fallbackAccountName?: string;
	onClose: () => void;
};

const GROUP_LABELS: Record<SocialCasinoGroupId, string> = {
	"1": "Group 1",
	"2": "Group 2",
};

const EMPTY_DRAFT: CertificatesDraft = { gambling: [], socialCasino: [] };

export const AccountDetailsModal = ({
	isOpen,
	accountId,
	fallbackAccountName = "",
	onClose,
}: Props) => {
	const query = useAccountCard(isOpen ? accountId : undefined);
	const saveMutation = useSaveAccountCertificates();

	const [draft, setDraft] = useState<CertificatesDraft>(EMPTY_DRAFT);
	const [baseline, setBaseline] = useState<CertificatesDraft>(EMPTY_DRAFT);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [socialAddBundleId, setSocialAddBundleId] = useState("");
	const [socialAddGroupIds, setSocialAddGroupIds] = useState<
		SocialCasinoGroupId[]
	>([]);
	const [socialAddError, setSocialAddError] = useState<string | null>(null);

	const priorityGeos = usePriorityGamblingGeos(isOpen);
	const extraGeosCatalog = useExtraGamblingGeos(isOpen);

	useEffect(() => {
		if (!query.data) return;
		const next = buildCertificatesDraft(query.data.certificates);
		setDraft(next);
		setBaseline(next);
		setSaveError(null);
		setSocialAddBundleId("");
		setSocialAddGroupIds([]);
		setSocialAddError(null);
		saveMutation.reset();
		// Intentionally seed only when card payload changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [query.data]);

	const displayName =
		query.data?.account_name?.trim() || fallbackAccountName || accountId;
	const displayId = formatAccountCardId(
		query.data?.account_id || accountId,
	);

	const gamblingChips = useMemo(
		() => gamblingChipGeos(draft.gambling, priorityGeos),
		[draft.gambling, priorityGeos],
	);
	const selectedGambling = useMemo(
		() => new Set(draft.gambling.map((g) => g.toUpperCase())),
		[draft.gambling],
	);
	const addGeoOptions = useMemo(
		() => extraGeosCatalog.filter((code) => !selectedGambling.has(code)),
		[extraGeosCatalog, selectedGambling],
	);

	const isDirty = !draftsEqual(draft, baseline);
	const isLoading = query.isPending || (query.isFetching && !query.data);
	const isError = query.isError;
	const errorStatus =
		query.error instanceof AccountCardFetchError ? query.error.status : 0;
	const loadErrorMessage =
		errorStatus === 404
			? "Обліковий запис не знайдено"
			: "Не вдалося завантажити дані облікового запису";

	const certPresence = useMemo(
		() =>
			resolveCertPresence({
				certificates: query.data?.certificates,
				collection: query.data?.collection,
				lastScrape: query.data?.last_scrape,
				metaReady: Boolean(query.data) || isError,
			}),
		[query.data, isError],
	);

	const gamblingCountLabel =
		draft.gambling.length === 0 ? "0 ГЕО" : `${draft.gambling.length} ГЕО`;

	const socialCountLabel =
		draft.socialCasino.length === 0
			? certPresence.kind === "unwired" || certPresence.kind === "unknown"
				? "даних немає"
				: "сертифікатів немає"
			: `${draft.socialCasino.length} сертифікат${draft.socialCasino.length === 1 ? "" : draft.socialCasino.length < 5 ? "и" : "ів"}`;

	const showPresenceBanner =
		certPresence.kind !== "has_certs" &&
		certPresence.kind !== "loading" &&
		Boolean(certPresence.hint);

	const showManualEditWarning =
		certPresence.kind === "unwired" || certPresence.kind === "unknown";

	const handleToggleGambling = (geo: string) => {
		setSaveError(null);
		setDraft((prev) => ({
			...prev,
			gambling: toggleGeo(prev.gambling, geo),
		}));
	};

	const handleAddExtraGeo = (code: string) => {
		const normalized = code.trim().toUpperCase();
		if (!/^[A-Z]{2}$/.test(normalized)) {
			setSaveError("GEO має бути ISO-кодом з 2 літер (напр. HU)");
			return;
		}
		setSaveError(null);
		setDraft((prev) => ({
			...prev,
			gambling: [...new Set([...prev.gambling, normalized])].sort((a, b) =>
				a.localeCompare(b),
			),
		}));
	};

	const handleToggleSocialGroup = (
		appBundleId: string,
		groupId: SocialCasinoGroupId,
	) => {
		setSaveError(null);
		setDraft((prev) => ({
			...prev,
			socialCasino: prev.socialCasino
				.map((entry) =>
					entry.appBundleId === appBundleId
						? toggleSocialGroup(entry, groupId)
						: entry,
				)
				.filter((e) => e.groupIds.length > 0),
		}));
	};

	const handleRemoveSocialBundle = (appBundleId: string) => {
		setSaveError(null);
		setSocialAddError(null);
		setDraft((prev) => ({
			...prev,
			socialCasino: prev.socialCasino.filter((e) => e.appBundleId !== appBundleId),
		}));
	};

	const toggleSocialAddGroup = (groupId: SocialCasinoGroupId) => {
		setSocialAddError(null);
		setSocialAddGroupIds((prev) => {
			if (prev.includes(groupId))
				return prev.filter((id) => id !== groupId);
			return [...prev, groupId].sort((a, b) => a.localeCompare(b));
		});
	};

	const handleAddSocialBundle = () => {
		setSaveError(null);
		setSocialAddError(null);

		const bundleId = socialAddBundleId.trim();
		const validation = validateAppBundleId(bundleId);
		if (!validation.ok) {
			setSocialAddError(validation.message);
			return;
		}
		if (socialAddGroupIds.length === 0) {
			setSocialAddError("Оберіть Group 1 або Group 2");
			return;
		}

		setDraft((prev) => {
			const existingIdx = prev.socialCasino.findIndex(
				(e) => e.appBundleId === bundleId,
			);
			let next = prev.socialCasino.slice();
			if (existingIdx >= 0) {
				next[existingIdx] = {
					...next[existingIdx],
					groupIds: socialAddGroupIds,
				};
			} else {
				next.push({
					appBundleId: bundleId,
					groupIds: socialAddGroupIds,
				});
			}
			next = next
				.filter((e) => e.groupIds.length > 0)
				.sort((a, b) => a.appBundleId.localeCompare(b.appBundleId));
			return { ...prev, socialCasino: next };
		});

		setSocialAddBundleId("");
		setSocialAddGroupIds([]);
	};

	const handleSave = async () => {
		setSaveError(null);
		try {
			const payload = buildSaveCertificatesPayload(accountId, draft);
			const result = await saveMutation.mutateAsync(payload);
			const next = buildCertificatesDraft(result.certificates);
			setDraft(next);
			setBaseline(next);
			onClose();
		} catch (error) {
			setSaveError(extractSaveCertificatesError(error));
		}
	};

	const socialAddRow = (
		<div className='account-details-modal__social-add-row'>
			{(["1", "2"] as SocialCasinoGroupId[]).map((groupId) => {
				const active = socialAddGroupIds.includes(groupId);
				const title =
					groupId === "1"
						? `Group 1: ${SOCIAL_CASINO_GROUP_1_ISO.join(" ")}`
						: `Group 2: ${SOCIAL_CASINO_GROUP_2_ISO.join(" ")}`;
				return (
					<button
						key={groupId}
						type='button'
						className={`account-details-modal__social-group${active ? " account-details-modal__social-group--active" : ""}`}
						aria-pressed={active}
						title={title}
						onClick={() => toggleSocialAddGroup(groupId)}>
						{GROUP_LABELS[groupId]}
					</button>
				);
			})}
			<input
				className='account-details-modal__bundle-input'
				value={socialAddBundleId}
				placeholder='com.example.app'
				aria-label='app bundle id'
				maxLength={MAX_APP_BUNDLE_ID_LENGTH}
				onChange={(e) => setSocialAddBundleId(e.target.value)}
			/>
			<button
				type='button'
				className='account-details-modal__social-add-btn'
				disabled={!socialAddBundleId.trim() || socialAddGroupIds.length === 0}
				onClick={handleAddSocialBundle}>
				Додати
			</button>
		</div>
	);

	let bodyContent: ReactNode;

	if (isLoading) {
		bodyContent = (
			<div className='account-details-modal__loader'>
				<CircleLoader />
			</div>
		);
	} else if (isError) {
		bodyContent = (
			<div className='account-details-modal__error'>
				<p>{loadErrorMessage}</p>
				<CompactButton
					type='button'
					theme='default'
					onClick={() => void query.refetch()}>
					Спробувати знову
				</CompactButton>
			</div>
		);
	} else {
		bodyContent = (
			<>
				{showPresenceBanner ? (
					<div
						className={`account-details-modal__presence account-details-modal__presence--${certPresence.kind}`}
						role='status'>
						<p className='account-details-modal__presence-text'>
							{certPresence.hint}
						</p>
						{showManualEditWarning ? (
							<p className='account-details-modal__presence-note'>
								Редагування доступне: дані вводяться вручну і автоматично не
								підтверджуватимуться.
							</p>
						) : null}
					</div>
				) : null}

				<section className='account-details-modal__section'>
					<div className='account-details-modal__section-head'>
						<span className='account-details-modal__section-label account-details-modal__section-label--gambling'>
							Гембл — ГЕО
						</span>
						<span className='account-details-modal__section-meta'>
							{gamblingCountLabel}
						</span>
					</div>
					<div className='account-details-modal__geo-chips'>
						{gamblingChips.map((geo) => {
							const active = selectedGambling.has(geo);
							return (
								<button
									key={geo}
									type='button'
									className={`account-details-modal__geo-chip${active ? " account-details-modal__geo-chip--active" : ""}`}
									aria-pressed={active}
									onClick={() => handleToggleGambling(geo)}>
									{geo}
								</button>
							);
						})}
					</div>
					<div className='account-details-modal__add-geo'>
						<GeoSearchSelect
							options={addGeoOptions}
							onSelect={handleAddExtraGeo}
							placeholder='+ add GEO'
						/>
					</div>
				</section>

				<section className='account-details-modal__section account-details-modal__section--social'>
					<div className='account-details-modal__section-head'>
						<span className='account-details-modal__section-label account-details-modal__section-label--social'>
							Соц казино
						</span>
						<span className='account-details-modal__section-meta'>
							{socialCountLabel}
						</span>
					</div>

					{draft.socialCasino.length === 0 ? (
						<div className='account-details-modal__social-empty-panel'>
							<p className='account-details-modal__empty'>
								{certPresence.kind === "unwired" ||
								certPresence.kind === "unknown"
									? "Соц. сертифікати невідомі — збір не дає даних"
									: "Немає соц. сертифікатів"}
							</p>
							{socialAddRow}
							{socialAddError ? (
								<p className='account-details-modal__save-error' role='alert'>
									{socialAddError}
								</p>
							) : null}
						</div>
					) : (
						<>
							<ul className='account-details-modal__social-list'>
								{draft.socialCasino.map((cert) => (
									<li
										key={cert.appBundleId}
										className='account-details-modal__social-item'>
										<div className='account-details-modal__social-bundle-row'>
											<span className='account-details-modal__social-bundle'>
												{cert.appBundleId}
											</span>
											<button
												type='button'
												className='account-details-modal__social-remove'
												aria-label='Remove social-casino certificate'
												onClick={() =>
													handleRemoveSocialBundle(cert.appBundleId)
												}>
												×
											</button>
										</div>
										<div className='account-details-modal__social-groups'>
											{(["1", "2"] as SocialCasinoGroupId[]).map((groupId) => {
												const active = cert.groupIds.includes(groupId);
												const title =
													groupId === "1"
														? `Group 1: ${SOCIAL_CASINO_GROUP_1_ISO.join(" ")}`
														: `Group 2: ${SOCIAL_CASINO_GROUP_2_ISO.join(" ")}`;
												return (
													<button
														key={groupId}
														type='button'
														className={`account-details-modal__social-group${active ? " account-details-modal__social-group--active" : ""}`}
														aria-pressed={active}
														title={title}
														onClick={() =>
															handleToggleSocialGroup(
																cert.appBundleId,
																groupId,
															)
														}>
														{GROUP_LABELS[groupId]}
													</button>
												);
											})}
										</div>
									</li>
								))}
							</ul>

							<div className='account-details-modal__social-add'>
								{socialAddRow}
								{socialAddError ? (
									<p
										className='account-details-modal__save-error'
										role='alert'>
										{socialAddError}
									</p>
								) : null}
							</div>
						</>
					)}
				</section>

				{saveError ? (
					<p className='account-details-modal__save-error' role='alert'>
						{saveError}
					</p>
				) : null}
			</>
		);
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			size='lg'
			title={
				<div className='account-details-modal__title'>
					<span className='account-details-modal__title-name'>{displayName}</span>
					<span className='account-details-modal__title-id'>{displayId}</span>
				</div>
			}
			footer={
				<div className='account-details-modal__footer'>
					<CompactButton type='button' theme='default' onClick={onClose}>
						Скасувати
					</CompactButton>
					{!isLoading && !isError ? (
						<CompactButton
							type='button'
							theme='green'
							unactive={!isDirty || saveMutation.isPending}
							onClick={() => void handleSave()}>
							{saveMutation.isPending ? "Збереження…" : "Зберегти"}
						</CompactButton>
					) : null}
				</div>
			}>
			<div className='account-details-modal'>{bodyContent}</div>
		</Modal>
	);
};
